<template>
<div>
	<div v-if="localQuestion !== null" class="question-card">
		<!-- Question Header -->
		<div class="question-header">
			<div class="d-flex justify-content-between align-items-start">
				<div class="question-content">
					<div v-if="!editMode">
						<h5 class="question-title">{{localQuestion.text}}</h5>
						<small class="text-muted">Fact card name: Q_{{localQuestion.key ? localQuestion.key.toUpperCase() : ''}}</small>
					</div>
				</div>
				<div class="question-actions">
					<b-button 
						@click="handleEditBtnClick" 
						:variant="editMode ? 'success' : 'outline-primary'"
						size="sm"
						class="mr-2"
					>
						<i :class="editMode ? 'fa fa-save' : 'fa fa-edit'" class="mr-1"></i>
						{{editMode ? "Save" : "Edit"}}
					</b-button>
					<b-button 
						v-if="questionnaireId !== null"
						@click="handleCancelRemove" 
						:variant="editMode ? 'outline-secondary' : 'outline-danger'"
						size="sm"
					>
						<i :class="editMode ? 'fa fa-times' : 'fa fa-trash'" class="mr-1"></i>
						{{editMode ? "Cancel" : "Remove"}}
					</b-button>
				</div>
			</div>
		</div>

		<!-- Question Content -->
		<div class="question-content">
			<!-- Display Mode -->
			<div v-if="!editMode">
				<div class="info-section">
					<div class="info-item">
						<strong>Type:</strong> {{switchAnswerType(question.questionType)}}
					</div>
					<div v-if="localQuestion.correctAnswer" class="info-item">
						<strong>Correct Answer:</strong> {{localQuestion.correctAnswer}}
					</div>
					<div v-if="localQuestion.explainer" class="info-item">
						<strong>Explainer:</strong> {{localQuestion.explainer}}
					</div>
				</div>

				<!-- Possible Answers -->
				<div v-if="localQuestion.possibleAnswers && localQuestion.possibleAnswers.length > 0" class="answers-section">
					<h6>Possible Answers:</h6>
					<div class="answers-list">
						<div v-for="a in orderedPossibleAnswers" :key="a.answerId" class="answer-item">
							{{a.answer.text}}
						</div>
					</div>
				</div>

				<!-- Additional Settings -->
				<div v-if="localQuestion.multipleChoice !== undefined" class="settings-section">
					<h6>Settings:</h6>
					<div class="settings-grid">
						<div class="setting-item">
							<span class="setting-label">Multiple Choice:</span>
							<span class="setting-value">{{localQuestion.multipleChoice ? 'Yes' : 'No'}}</span>
						</div>
						<div class="setting-item">
							<span class="setting-label">Out Of:</span>
							<span class="setting-value">{{localQuestion.outOf ? 'Yes' : 'No'}}</span>
						</div>
						<div class="setting-item">
							<span class="setting-label">Min Amount:</span>
							<span class="setting-value">{{localQuestion.minAmount}}</span>
						</div>
						<div class="setting-item">
							<span class="setting-label">Max Amount:</span>
							<span class="setting-value">{{localQuestion.maxAmount}}</span>
						</div>
						<div class="setting-item">
							<span class="setting-label">Default Amount:</span>
							<span class="setting-value">{{localQuestion.defaultAmount}}</span>
						</div>
						<div class="setting-item">
							<span class="setting-label">Min Label:</span>
							<span class="setting-value">{{localQuestion.minLabel}}</span>
						</div>
						<div class="setting-item">
							<span class="setting-label">Max Label:</span>
							<span class="setting-value">{{localQuestion.maxLabel}}</span>
						</div>
					</div>
				</div>
			</div>

			<!-- Edit Mode -->
			<div v-else>
				<!-- Simplified form for new questions -->
				<div v-if="isNewQuestion" class="simplified-form">
					<!-- Question Text -->
					<b-form-group label="Question Text:" class="mb-3">
						<b-form-input 
							v-model="localQuestion.text" 
							placeholder="Enter your question"
						></b-form-input>
					</b-form-group>

					<!-- Question Key -->
					<b-form-group label="Question Key:" class="mb-3">
						<b-form-input 
							v-model="localQuestion.key" 
							placeholder="Enter question key"
							maxlength="10"
							@input="filterKeyInput"
						></b-form-input>
					</b-form-group>

					<!-- Fact card name display (not editable) -->
					<p class="text-muted mb-3">Fact card name: Q_{{localQuestion.key ? localQuestion.key.toUpperCase() : ''}}</p>

					<!-- Correct Quiz Answer -->
					<b-form-group label="Correct Quiz Answer:" class="mb-3">
						<b-form-input 
							v-model="localQuestion.correctAnswer" 
							placeholder="Enter correct answer"
						></b-form-input>
					</b-form-group>

					<!-- Explainer Quiz Answer -->
					<b-form-group label="Explainer Quiz Answer:" class="mb-3">
						<b-form-input 
							v-model="localQuestion.explainer" 
							placeholder="Enter explainer answer"
						></b-form-input>
					</b-form-group>

					<!-- Answer Type -->
					<b-form-group label="Answer type:" class="mb-4">
						<b-form-select
							v-model="localQuestion.questionType"
							:options="questionTypeOptions"
							required
						></b-form-select>
					</b-form-group>

					<!-- Possible Answers (for choice questions) -->
					<div v-if="localQuestion.questionType == 4 || localQuestion.questionType == 5" class="answers-edit-section">
						<h6>Possible Answers</h6>
						<div v-if="orderedPossibleAnswers && orderedPossibleAnswers.length > 0" class="mb-3">
							<div v-for="(answer, index) in orderedPossibleAnswers" :key="index" class="answer-edit-item" draggable @dragstart="pickupAnswer($event, answer, index)" @dragover.prevent="showAnswerDropPlace($event, answer, index)" @dragenter.prevent @drop="moveAnswer($event, answer, index)" @dragend.prevent="dragEndClear">
								<div class="d-flex align-items-center">
									<!-- Drag Handle -->
									<div class="answer-drag-handle mr-2">
										⋮⋮
									</div>
									
									<b-form-input
										v-model="answer.answer.text"
										placeholder="Answer text"
										class="flex-grow-1 mr-2"
									></b-form-input>
									<b-form-input
										v-model="answer.answer.value"
										placeholder="Value"
										class="mr-2"
										style="width: 100px;"
									></b-form-input>
									<b-button 
										variant="outline-danger" 
										size="sm"
										@click="removeAnswerFromQuestion(localQuestion, answer)"
										title="Remove Answer"
										class="symbol-button delete-button"
									>
										×
									</b-button>
								</div>
							</div>
						</div>
						<div class="add-answer-section">
							<b-row>
								<b-col md="5">
									<b-form-input 
										v-model="answerText" 
										placeholder="Answer text"
										class="mb-2"
									></b-form-input>
								</b-col>
								<b-col md="5">
									<b-form-input 
										v-model="answerValue" 
										placeholder="Value"
										class="mb-2"
									></b-form-input>
								</b-col>
								<b-col md="2">
									<b-button 
										@click="addAnswerToQuestion()" 
										variant="success" 
										size="sm"
										block
										class="symbol-button add-button"
									>
										+
									</b-button>
								</b-col>
							</b-row>
						</div>
					</div>
				</div>

				<!-- Full form for editing existing questions -->
				<div v-else>
					<!-- Question Text -->
					<b-form-group label="Question Text" class="mb-4">
						<b-form-input 
							v-model="localQuestion.text" 
							placeholder="Enter your question"
						></b-form-input>
					</b-form-group>

					<!-- Question Key -->
					<b-form-group label="Question Key" class="mb-4">
						<b-form-input 
							v-model="localQuestion.key" 
							placeholder="Enter question key"
							maxlength="10"
							@input="filterKeyInput"
						></b-form-input>
					</b-form-group>

					<!-- Fact card name display -->
					<p class="text-muted mb-4">Fact card name: Q_{{localQuestion.key ? localQuestion.key.toUpperCase() : ''}}</p>

					<!-- Question Type -->
					<b-form-group label="Question Type" class="mb-4">
						<b-form-select
							v-model="localQuestion.questionType"
							:options="questionTypeOptions"
							required
						></b-form-select>
					</b-form-group>

					<!-- Correct Answer -->
					<b-form-group label="Correct Answer" class="mb-4">
						<b-form-input 
							v-model="localQuestion.correctAnswer" 
							placeholder="Enter correct answer"
						></b-form-input>
					</b-form-group>

					<!-- Explainer Answer -->
					<b-form-group label="Explainer Answer" class="mb-4">
						<b-form-input 
							v-model="localQuestion.explainer" 
							placeholder="Enter explainer answer"
						></b-form-input>
					</b-form-group>

					<!-- Additional Settings -->
					<div class="settings-edit-section">
						<h6>Additional Settings</h6>
						<b-row>
							<b-col md="6">
								<b-form-group label="Multiple Choice">
									<b-form-checkbox v-model="localQuestion.multipleChoice"/>
								</b-form-group>
							</b-col>
							<b-col md="6">
								<b-form-group label="Out Of">
									<b-form-checkbox v-model="localQuestion.outOf"/>
								</b-form-group>
							</b-col>
						</b-row>
						<b-row>
							<b-col md="4">
								<b-form-group label="Min Amount">
									<b-form-input 
										v-model.number="localQuestion.minAmount" 
										type="number"
										placeholder="0"
									></b-form-input>
								</b-form-group>
							</b-col>
							<b-col md="4">
								<b-form-group label="Max Amount">
									<b-form-input 
										v-model.number="localQuestion.maxAmount" 
										type="number"
										placeholder="7"
									></b-form-input>
								</b-form-group>
							</b-col>
							<b-col md="4">
								<b-form-group label="Default Amount">
									<b-form-input 
										v-model.number="localQuestion.defaultAmount" 
										type="number"
										placeholder="0"
									></b-form-input>
								</b-form-group>
							</b-col>
						</b-row>
						<b-row>
							<b-col md="6">
								<b-form-group label="Min Label">
									<b-form-input 
										v-model="localQuestion.minLabel" 
										placeholder="0"
									></b-form-input>
								</b-form-group>
							</b-col>
							<b-col md="6">
								<b-form-group label="Max Label">
									<b-form-input 
										v-model="localQuestion.maxLabel" 
									></b-form-input>
								</b-form-group>
							</b-col>
						</b-row>
					</div>

					<!-- Possible Answers (for choice questions) -->
					<div v-if="localQuestion.questionType == 4 || localQuestion.questionType == 5" class="answers-edit-section">
						<h6>Possible Answers (Text/Value)</h6>
						<div v-if="orderedPossibleAnswers && orderedPossibleAnswers.length > 0" class="mb-3">
							<div v-for="(answer, index) in orderedPossibleAnswers" :key="index" class="answer-edit-item" draggable @dragstart="pickupAnswer($event, answer, index)" @dragover.prevent="showAnswerDropPlace($event, answer, index)" @dragenter.prevent @drop="moveAnswer($event, answer, index)" @dragend.prevent="dragEndClear">
								<div class="d-flex align-items-center">
									<!-- Drag Handle -->
									<div class="answer-drag-handle mr-2">
										⋮⋮
									</div>
									
									<b-form-input
										v-model="answer.answer.text"
										placeholder="Answer text"
										class="flex-grow-1 mr-2"
									></b-form-input>
									<b-form-input
										v-model="answer.answer.value"
										placeholder="Value"
										class="mr-2"
										style="width: 100px;"
									></b-form-input>
									<b-button 
										variant="outline-danger" 
										size="sm"
										@click="removeAnswerFromQuestion(localQuestion, answer)"
										title="Remove Answer"
										class="symbol-button delete-button"
									>
										×
									</b-button>
								</div>
							</div>
						</div>
						<div class="add-answer-section">
							<b-row>
								<b-col md="5">
									<b-form-input 
										v-model="answerText" 
										placeholder="Answer text"
										class="mb-2"
									></b-form-input>
								</b-col>
								<b-col md="5">
									<b-form-input 
										v-model="answerValue" 
										placeholder="Value"
										class="mb-2"
									></b-form-input>
								</b-col>
								<b-col md="2">
									<b-button 
										@click="addAnswerToQuestion()" 
										variant="success" 
										size="sm"
										block
										class="symbol-button add-button"
									>
										+
									</b-button>
								</b-col>
							</b-row>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</div>
</template>

<script>
export default {
  name: 'QuestionCard',
  props: {
	question: {
		type: Object,
		required: true
	},
	questionnaireId: {
		type: String, 
		required: false
	},
	initEditMode: {
		type: Boolean,
		required: false,
		default: false
	},
	isNewQuestion: {
		type: Boolean,
		required: false,
		default: false
	}
  },
  data() {
	return{
		createNewQuestion: false,
		editMode: false,
		answerText: null,
		correctAnswer: "",
		explainer: "",
		answerValue: null,
		delMethod: null,
		localQuestion: null,
		dragedAnswer: null,
		overAnswer: null
	}
  },
  computed: {
	questionTypeOptions() {
		return [
			{ value: 0, text: 'Free text' },
			{ value: 1, text: 'Free text (number)' },
			{ value: 2, text: 'Free text (currency)' },
			{ value: 3, text: 'Free text (%)' },
			{ value: 4, text: 'Multiple choice' },
			{ value: 5, text: 'Dropdown' }
		];
	},
	orderedPossibleAnswers() {
		if (!this.localQuestion.possibleAnswers) return [];
		// Sort answers by their order field
		return [...this.localQuestion.possibleAnswers].sort((a, b) => {
			const orderA = a.order !== undefined ? a.order : 0;
			const orderB = b.order !== undefined ? b.order : 0;
			return orderA - orderB;
		});
	}
  },
  watch: {
	question: {
		handler(newVal) {
			this.localQuestion = JSON.parse(JSON.stringify(newVal));
		},
		deep: true
	}
  },
  methods: {
	handleEditBtnClick: function() {
		console.log("edit button clicked");
		if (this.editMode) {
			this.saveQuestion();
			this.editMode = false;
		}
		else {
			this.editMode = true;
		}
	},
	handleCancelRemove: function() {
		console.log("cancel remove question");
		console.log(this.localQuestion.text);
		if (this.editMode) {
			if (this.localQuestion.id == null || this.localQuestion.id == '') {
				this.$emit("cancelNewQuestion");
			}
			this.editMode = false;
			this.localQuestion = JSON.parse(JSON.stringify(this.$props.question));
		} else {
			if (this.$props.questionnaireId != null) {
				this.removeQuestionFromQuestionnaire();
			} else {
				
				this.$http.delete(this.$config.ApiUri + "/Questions/" + this.localQuestion.id)
				.then(() => {
					this.$emit("onChange");
					this.$emit("onMessage", "Deleted question");
				})
				.catch((e) => {
					console.log("Error deleting question");
					console.log(e);
					this.$emit("onError", e);
				});
			}
		}
	},
	saveQuestion: function(){
		// Initialize new fields with default values if they don't exist
		// if (this.localQuestion.multipleChoice === undefined) this.localQuestion.multipleChoice = false;
		// if (this.localQuestion.minAmount === undefined) this.localQuestion.minAmount = 0;
		// if (this.localQuestion.maxAmount === undefined) this.localQuestion.maxAmount = 7;
		// if (this.localQuestion.defaultAmount === undefined) this.localQuestion.defaultAmount = 0;
		// if (this.localQuestion.minLabel === undefined) this.localQuestion.minLabel = null;
		// if (this.localQuestion.maxLabel === undefined) this.localQuestion.maxLabel = null;
		// if (this.localQuestion.outOf === undefined) this.localQuestion.outOf = false;
				
		if (this.localQuestion.id == null || this.localQuestion.id == "") {
			delete this.localQuestion.id;
			this.$http.post(this.$config.ApiUri + "/Questions", this.localQuestion)
				.then((result) => {
					this.localQuestion = result.data;
					var addToQuestionnaire = this.$props.questionnaireId != null || this.$props.questionnaireId != '';
					if (addToQuestionnaire) { //addToQuestionnaire was originally a bool param to this method
						this.addQuestionToQuestionnaire();
					}
					this.$emit("onCreateNew");
					this.$emit("onMessage", "Created question");
				}
			).catch((e) => {
				console.log("Error creating question");
				console.log(e);
				this.$emit("onError", e);
			});
		} else {
			console.log("updating question", this.localQuestion);
			this.$http.put(this.$config.ApiUri + "/Questions/" + this.localQuestion.id, this.localQuestion)
				.then((result) => {
					this.localQuestion = result.data;
					this.$emit("onChange");
					this.$emit("onMessage", "Updated question");
				}
			).catch((e) => { 
				console.log("Error saving question");
				console.log(e);
				this.$emit("onError", e);
			});
		}
	},
	// TODO: Separate out onChange and onCreateNew for rebuild purposes in parent
	addQuestionToQuestionnaire: function() {
		if (this.$props.questionnaireId != null && this.$props.questionnaireId != "") {
			if (this.localQuestion.id != null && this.localQuestion.id != "") {
				this.$http.post(this.$config.ApiUri + "/Questionnaires/" + this.$props.questionnaireId + "/QuestionnaireQuestions/" + this.localQuestion.id)
					.then(() => {
						this.$emit("onChange");
					}
				).catch((e) => { 
					console.log("Error adding question from questionnaire ");
					console.log(e);
					this.$emit("onError", e);
				});
			}
		} 
	},
	removeQuestionFromQuestionnaire: function() {
		if (this.$props.questionnaireId != null && this.$props.questionnaireId != "") {
			this.$http.delete(this.$config.ApiUri + "/Questionnaires/" + this.$props.questionnaireId + "/QuestionnaireQuestions/" + this.localQuestion.id)
				.then(() => {
					this.$emit("onChange");
					this.$emit("onMessage", "Removed question from questionnaire");
				}
			).catch((e) => { 
				console.log("Error removing question from questionnaire ");
				console.log(e);
				this.$emit("onError", e);
			});
		}
	},
	addAnswerToQuestion: function() {
		const newOrder = this.localQuestion.possibleAnswers ? this.localQuestion.possibleAnswers.length : 0;
		this.localQuestion.possibleAnswers.push({ 
			"answer": { 
				"text": this.answerText, 
				"value": this.answerValue, 
				"correctAnswer": this.correctAnswer,
				"explainer": this.explainer
			},
			"order": newOrder
		});
		this.answerText = null;
		this.answerValue = null;
		this.correctAnswer = null;
		this.explainer = null;
	},
	removeAnswerFromQuestion: function(qu, a) {
		var i = qu.possibleAnswers.indexOf(a);
		qu.possibleAnswers.splice(i, 1);
	},
	switchAnswerType(type) {
		switch(type) {
			case 0: return "Free text";
			case 1: return "Free text (number)";
			case 2: return "Free text (currency)";
			case 3: return "Free text (%)";
			case 4: return "Multiple choice";
			case 5: return "Dropdown";
			default: return "Unknown"
		}
	},
	filterKeyInput(value) {
		this.localQuestion.key = value.replace(/\s+/g, '');
	},
	pickupAnswer(event, answer, index) {
		console.log("pick up answer");
		console.log(answer);
		event.dataTransfer.dropEffect = "move";
		this.dragedAnswer = { ...answer, index: index };
	},
	showAnswerDropPlace(event, answer, index) {
		if (answer !== this.dragedAnswer) {
			this.overAnswer = { ...answer, index: index };
		} else {
			this.overAnswer = null;
		}
	},
	moveAnswer(event, answer, index) {
		console.log(`moveAnswer: event: ${event} | answer: ${answer} | index: ${index}`);
		if (this.dragedAnswer && this.dragedAnswer.index !== index) {
			const answers = this.localQuestion.possibleAnswers;
			const movedAnswer = answers.splice(this.dragedAnswer.index, 1)[0];
			answers.splice(index, 0, movedAnswer);
			
			// Update the order property for all answers to reflect their new positions
			answers.forEach((answer, newIndex) => {
				if (answer.order !== undefined) {
					answer.order = newIndex;
				}
			});
		}
	},
	dragEndClear() {
		console.log("dragEnd");
		this.dragedAnswer = null;
		this.overAnswer = null;
	}
  },
  mounted: function() {
	this.localQuestion = JSON.parse(JSON.stringify(this.$props.question));
	
	// Initialize new fields with default values if they don't exist
	// if (this.localQuestion.multipleChoice === undefined) this.localQuestion.multipleChoice = false;
	// if (this.localQuestion.minAmount === undefined) this.localQuestion.minAmount = 0;
	// if (this.localQuestion.maxAmount === undefined) this.localQuestion.maxAmount = 7;
	// if (this.localQuestion.defaultAmount === undefined) this.localQuestion.defaultAmount = 0;
	// if (this.localQuestion.minLabel === undefined) this.localQuestion.minLabel = "0";
	// if (this.localQuestion.maxLabel === undefined) this.localQuestion.maxLabel = "7+";
	// if (this.localQuestion.outOf === undefined) this.localQuestion.outOf = false;
	
	// Ensure all possible answers have an order property
	if (this.localQuestion.possibleAnswers && this.localQuestion.possibleAnswers.length > 0) {
		this.localQuestion.possibleAnswers.forEach((answer, index) => {
			if (answer.order === undefined) {
				answer.order = index;
			}
		});
	}
	
	this.editMode = this.$props.initEditMode;
  }
}
</script>

<style scoped>
.question-card {
	background-color: white;
	border: 1px solid #dee2e6;
	border-radius: 8px;
	overflow: hidden;
	box-shadow: 0 2px 4px rgba(0,0,0,0.1);
	margin-bottom: 16px;
	transition: all 0.2s;
}

.question-card:hover {
	box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

.question-header {
	padding: 16px 20px;
	background-color: #f8f9fa;
	border-bottom: 1px solid #dee2e6;
}

.question-title {
	margin: 0;
	font-size: 1.1rem;
	font-weight: 500;
	color: #212529;
}

.question-text-input {
	font-size: 1.1rem;
	font-weight: 500;
}

.question-actions {
	white-space: nowrap;
}

.question-actions .btn {
	margin-right: 8px;
}

.question-actions .btn:last-child {
	margin-right: 0;
}

.question-content {
	padding: 20px;
}

.info-section {
	margin-bottom: 20px;
}

.info-item {
	margin-bottom: 8px;
	font-size: 0.9rem;
}

.answers-section {
	margin-bottom: 20px;
}

.answers-section h6 {
	margin-bottom: 12px;
	font-weight: 500;
	color: #495057;
}

.answers-list {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.answer-item {
	padding: 8px 12px;
	background-color: #f8f9fa;
	border: 1px solid #e9ecef;
	border-radius: 4px;
	font-size: 0.9rem;
}

.settings-section {
	margin-bottom: 20px;
}

.settings-section h6 {
	margin-bottom: 12px;
	font-weight: 500;
	color: #495057;
}

.settings-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
	gap: 12px;
}

.setting-item {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 8px 12px;
	background-color: #f8f9fa;
	border: 1px solid #e9ecef;
	border-radius: 4px;
	font-size: 0.9rem;
}

.setting-label {
	font-weight: 500;
	color: #495057;
}

.setting-value {
	color: #6c757d;
}

.settings-edit-section {
	background-color: #f8f9fa;
	padding: 16px;
	border-radius: 6px;
	border: 1px solid #dee2e6;
	margin-bottom: 20px;
}

.settings-edit-section h6 {
	margin-bottom: 16px;
	font-weight: 500;
	color: #495057;
}

.answers-edit-section {
	background-color: #f8f9fa;
	padding: 16px;
	border-radius: 6px;
	border: 1px solid #dee2e6;
}

.answers-edit-section h6 {
	margin-bottom: 16px;
	font-weight: 500;
	color: #495057;
}

.answer-edit-item {
	background-color: white;
	padding: 12px;
	border-radius: 4px;
	border: 1px solid #dee2e6;
	margin-bottom: 8px;
	cursor: move;
	transition: all 0.2s ease;
}

.answer-edit-item:hover {
	background-color: #f8f9fa;
	border-color: #adb5bd;
}

.answer-drag-handle {
	color: #6c757d;
	cursor: grab;
	padding: 2px 6px;
	border-radius: 3px;
	transition: all 0.2s ease;
	user-select: none;
	font-size: 12px;
	font-weight: bold;
	background-color: #f8f9fa;
	border: 1px solid #dee2e6;
}

.answer-drag-handle:hover {
	color: #007bff;
	background-color: #e9ecef;
}

.answer-drag-handle:active {
	cursor: grabbing;
}

.add-answer-section {
	padding-top: 12px;
	border-top: 1px solid #dee2e6;
}

.simplified-form {
	background-color: #f8f9fa;
	padding: 20px;
	border-radius: 8px;
	border: 1px solid #dee2e6;
}

.simplified-form .form-group label {
	font-weight: 600;
	color: #495057;
}

.simplified-form .form-control {
	border-radius: 6px;
}

.simplified-form .form-control:focus {
	border-color: #007bff;
	box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* Responsive adjustments */
@media (max-width: 768px) {
	.question-header .d-flex {
		flex-direction: column;
		align-items: flex-start !important;
	}
	
	.question-actions {
		margin-top: 12px;
		width: 100%;
	}
	
	.question-actions .btn {
		flex: 1;
		margin-right: 8px;
	}
	
	.question-actions .btn:last-child {
		margin-right: 0;
	}
	
	.settings-grid {
		grid-template-columns: 1fr;
	}
}

/* Symbol button styling */
.symbol-button {
	min-width: 24px !important;
	width: 24px !important;
	height: 24px !important;
	padding: 0 !important;
	display: inline-flex !important;
	align-items: center !important;
	justify-content: center !important;
	border-radius: 3px !important;
	border: 1px solid !important;
	font-size: 16px !important;
	font-weight: bold !important;
	background-color: transparent !important;
	transition: all 0.2s ease !important;
}

.symbol-button.delete-button {
	border-color: #dc3545 !important;
	color: #dc3545 !important;
}

.symbol-button.delete-button:hover {
	background-color: #dc3545 !important;
	color: white !important;
}

.symbol-button.add-button {
	border-color: #28a745 !important;
	color: #28a745 !important;
}

.symbol-button.add-button:hover {
	background-color: #28a745 !important;
	color: white !important;
}
</style>